import { HashRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { ContentfulLivePreviewProvider } from "@contentful/live-preview/react";
import { SiteDataProvider } from "./context/SiteDataContext";

// Defines @font-face (Lekton, Poppins) and the base .App/#root layout rules —
// must live in the non-lazy root bundle so it loads on every route, not just
// whichever page happens to be visited first.
import "./App.css";

import Nav from "./components/layout/Nav";
import Footer from "./components/layout/Footer";
import ErrorBoundary from "./components/atoms/ErrorBoundary";
import OfflineBanner from "./components/atoms/OfflineBanner";
import RouteLoadingFallback from "./components/atoms/RouteLoadingFallback";
import ScrollToTopButton from "./components/atoms/ScrollToTopButton";

// pages — lazy-loaded per route so e.g. the blog page's dependencies don't
// bloat the initial bundle for visitors who only ever see the home page.
const Home = lazy(() => import("./pages/Home"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

function App() {
  return (
    <HelmetProvider>
      <HashRouter>
        <main className="App" id="website">
          <a
            href="#main-content"
            className="skip-link"
            title="Skip the navigation and jump to the main content"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("main-content")?.focus();
            }}
          >
            Skip to content
          </a>
          <OfflineBanner />
          <ContentfulLivePreviewProvider
            locale="en-US"
            enableInspectorMode={true}
            enableLiveUpdates={true}>
            <SiteDataProvider>
              <ErrorBoundary section="navigation">
                <Nav />
              </ErrorBoundary>
              <ErrorBoundary section="page content">
                <Suspense fallback={<RouteLoadingFallback />}>
                  <div id="main-content" tabIndex={-1}>
                    <Routes>
                      <Route index element={<Home />} />
                      <Route path="home" element={<Home />} />
                      <Route path="blog" element={<BlogPage />} />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </div>
                </Suspense>
              </ErrorBoundary>
              <ErrorBoundary section="footer">
                <Footer />
              </ErrorBoundary>
              <ErrorBoundary section="scroll to top" fallback={null}>
                <ScrollToTopButton />
              </ErrorBoundary>
            </SiteDataProvider>
          </ContentfulLivePreviewProvider>
        </main>
      </HashRouter>
    </HelmetProvider>
  );
}

export default App;
